// client/src/components/pages/LexiStar/ReviewQueue/ReviewQueue.tsx (converted from .jsx, ST-00204)
//
// LexiStar review queue (ST-00188, M3-U1; T4-S11; LS-09 §5.18). Route
// /lexistar/review, page permission lexistar.review.triage.
//
// "Nothing doubtful is counted silently": every derived record below 70%
// confidence, unresolved registration, low-confidence ticket match, ambiguous
// re-anchor, unplanned load and change to a signed-off trip waits here,
// excluded from statements until a person decides.
//
//   Header      open items · oldest · share of this week's records that needed
//               review (the number a manager watches fall after commissioning);
//               live via `lexistar:review` on tenant:{id}:review.
//   Causes      open items that share a cause (e.g. every arrival at one leaking
//               geofence) — triage the lot with one decision and a confirmed count.
//   List        sorted by age × impact (assuranceShared.priorityScore).
//   Side panel  map evidence ± 5 min, confidence breakdown by factor, one-tap
//               Accept / Correct / Dismiss (ReviewItemPanel).
//
// Everything the view shows lives in the URL (?state, kind, cause, trip, asset,
// from, to, item) so a notification or the trip detail can deep-link into it.
// Phone (≥ 360 px): the list, and a tap opens the item full width with a Back
// button. Keyboard: A accept · C correct · D dismiss · J/K next/previous.
//
// ST-00204: the "Opened" date filter's clear "×" and its "Any time" button are 44 px targets on
// a phone or touch screen (the picker itself opens the shared bottom sheet there).

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaClipboardCheck, FaSyncAlt, FaKeyboard, FaLayerGroup, FaTimes } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import { SegmentedControl, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { toApiDateString } from '../../../../utils/dateUtils';
import {
  listReviewItems as listReviewItemsJs,
  acceptReviewItem as acceptReviewItemJs,
  correctReviewItem as correctReviewItemJs,
  dismissReviewItem as dismissReviewItemJs,
  bulkResolveReviewItems as bulkResolveReviewItemsJs,
  pickList as pickListJs,
} from '../../../../services/lexistarAssurance';
import {
  PageHeader,
  ErrorBanner,
  NoticeBanner,
  LoadingState,
  EmptyState,
  NoPermissionPage,
  ConnectionIndicator,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtDuration as fmtDurationJs, secondsSince as secondsSinceJs } from '../StatusBoard/format';
import {
  REVIEW_KINDS as REVIEW_KINDS_JS,
  REVIEW_STATES as REVIEW_STATES_JS,
  IMPACT_META as IMPACT_META_JS,
  SUBJECT_LABELS as SUBJECT_LABELS_JS,
  sortReviewQueue as sortReviewQueueJs,
  causeGroups as causeGroupsJs,
  causeLabel as causeLabelJs,
  readReviewStats as readReviewStatsJs,
  fmtShare as fmtShareJs,
  errorMessage as errorMessageJs,
  reviewShortcut as reviewShortcutJs,
} from './assuranceShared';
import {
  ConfidenceChip as ConfidenceChipJs,
  ReviewStateChip as ReviewStateChipJs,
  KpiCard as KpiCardJs,
  useAssuranceEvents as useAssuranceEventsJs,
  useIsWide as useIsWideJs,
} from './assuranceAtoms';
import ReviewItemPanelJs from './ReviewItemPanel';
import BulkTriageDialogJs from './BulkTriageDialog';

// ── Shapes (GET /lexistar/review-items; m3-contracts) ──────────────────────

/** A review item row. */
export interface ReviewItem {
  id: string;
  kind: string;
  state?: string | null;
  subject_type?: string;
  subject_id?: string;
  asset_key?: string | null;
  confidence?: number | null;
  opened_at?: string;
  resolved_at?: string | null;
  cause_key?: string | null;
  impact?: string;
  [key: string]: unknown;
}

/** Queue header figures (assuranceShared.readReviewStats). */
interface ReviewStats {
  open?: number | null;
  oldest_at?: string | null;
  share_this_week?: number | null;
  [key: string]: unknown;
}

interface CauseGroup {
  cause_key: string;
  count: number;
  label: string;
}

/** What the bulk dialog triages: every open item with a cause, or a selection. */
interface BulkTarget {
  cause_key?: string;
  ids?: string[];
  label: string;
  count: number;
  counting?: boolean;
}

type Outcome = 'accept' | 'correct' | 'dismiss';
type Shortcut = Outcome | 'next' | 'previous';

// ── Typed views of the JS modules ───────────────────────────────────────────

const REVIEW_KINDS = REVIEW_KINDS_JS as Record<string, { label: string; short: string }>;
const REVIEW_STATES = REVIEW_STATES_JS as Array<{ value: string; label: string; states: string[] }>;
const IMPACT_META = IMPACT_META_JS as Record<string, { label: string; weight: number; help: string }>;
const SUBJECT_LABELS = SUBJECT_LABELS_JS as Record<string, string>;
const sortReviewQueue = sortReviewQueueJs as unknown as (items: ReviewItem[], now?: number) => ReviewItem[];
const causeGroups = causeGroupsJs as unknown as (items: ReviewItem[]) => CauseGroup[];
const causeLabel = causeLabelJs as unknown as (causeKey: string) => string;
const readReviewStats = readReviewStatsJs as unknown as (res: unknown, items?: ReviewItem[]) => ReviewStats;
const fmtShare = fmtShareJs as unknown as (ratio: number | null | undefined) => string;
const errorMessage = errorMessageJs as unknown as (err: unknown, fallback?: string) => string;
const reviewShortcut = reviewShortcutJs as unknown as (event: KeyboardEvent) => Shortcut | null;
const fmtDuration = fmtDurationJs as unknown as (seconds: number | null) => string;
const secondsSince = secondsSinceJs as unknown as (iso: string | null | undefined, now?: number) => number | null;
const pickList = pickListJs as unknown as <T>(res: unknown, ...keys: string[]) => T[];
const listReviewItems = listReviewItemsJs as unknown as (params: Record<string, unknown>) => Promise<unknown>;
const acceptReviewItem = acceptReviewItemJs as unknown as (id: string, payload?: object) => Promise<unknown>;
const dismissReviewItem = dismissReviewItemJs as unknown as (id: string, payload: object) => Promise<unknown>;
const correctReviewItem = correctReviewItemJs as unknown as (id: string, payload: object) => Promise<unknown>;
const bulkResolveReviewItems = bulkResolveReviewItemsJs as unknown as (
  body: { cause_key?: string; ids?: string[]; outcome: string; reason?: string }
) => Promise<Record<string, unknown> | null | undefined>;
const useAssuranceEvents = useAssuranceEventsJs as unknown as (opts: {
  review?: boolean;
  enabled?: boolean;
  onReview?: (msg: Record<string, unknown> | null) => void;
}) => string;
const useIsWide = useIsWideJs as unknown as (query?: string) => boolean;
const ConfidenceChip = ConfidenceChipJs as unknown as ComponentType<{
  confidence?: number | null;
  showLabel?: boolean;
}>;
const ReviewStateChip = ReviewStateChipJs as unknown as ComponentType<{ state?: string | null }>;
const KpiCard = KpiCardJs as unknown as ComponentType<{
  label: string;
  value: ReactNode;
  hint?: string;
  shortHint?: string;
}>;
const ReviewItemPanel = ReviewItemPanelJs as unknown as ComponentType<{
  item: ReviewItem;
  canTriage: boolean;
  pending: string | null;
  setPending: (p: string | null) => void;
  busy: boolean;
  actionError: string | null;
  onResolve: (outcome: Outcome, payload: object) => void;
  onBack?: () => void;
  now: number;
}>;
const BulkTriageDialog = BulkTriageDialogJs as unknown as ComponentType<{
  open: boolean;
  target: BulkTarget | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (choice: { outcome: string; reason?: string }) => void;
}>;

const LIVE_REFETCH_DEBOUNCE_MS = 1200;
const PAGE_LIMIT = 200;

const VERBS: Record<Outcome, string> = { accept: 'Accepted', correct: 'Corrected', dismiss: 'Dismissed' };

/** "YYYY-MM-DD" → local-midnight Date (never UTC-shifted), or null. */
const parseLocalDate = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

function QueueRow({
  row,
  selected,
  checked,
  onToggle,
  onSelect,
  now,
  selectable,
}: {
  row: ReviewItem;
  selected: boolean;
  checked: boolean;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  now: number;
  selectable: boolean;
}) {
  const impact = IMPACT_META[row.impact ?? ''] || IMPACT_META.report!;
  return (
    <li className={`flex items-stretch ${selected ? 'bg-primary-50' : ''}`}>
      {selectable && (
        <label
          className="flex items-center pl-3 pr-1 cursor-pointer"
          title="Select for bulk triage"
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(row.id)}
            aria-label={`Select ${REVIEW_KINDS[row.kind]?.short || row.kind} ${row.subject_id}`}
            className="w-5 h-5 rounded border-slate-300"
          />
        </label>
      )}
      <button
        type="button"
        onClick={() => onSelect(row.id)}
        aria-current={selected ? 'true' : undefined}
        className={`flex-1 min-w-0 text-left px-3 py-3 min-h-[64px] border-l-4 transition-colors ${selected ? 'border-l-primary-500' : 'border-l-transparent hover:bg-slate-50'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-slate-900 truncate">
            {REVIEW_KINDS[row.kind]?.short || row.kind}
          </span>
          {row.state && row.state !== 'open' ? (
            <ReviewStateChip state={row.state} />
          ) : (
            <ConfidenceChip confidence={row.confidence} showLabel={false} />
          )}
        </div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="text-sm font-mono text-slate-800 truncate">
            {row.asset_key
              ? String(row.asset_key).replace(/^reg:/, '')
              : row.subject_type === 'registration'
                ? row.subject_id
                : SUBJECT_LABELS[row.subject_type ?? '']}
          </span>
          <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
            {fmtDuration(secondsSince(row.opened_at, now))} ago
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-slate-500">
          <span className="truncate">{causeLabel(row.cause_key ?? '')}</span>
          <span
            className={`whitespace-nowrap font-medium ${row.impact === 'statement' ? 'text-slate-900' : ''}`}
            title={impact.help}
          >
            {impact.label}
          </span>
        </div>
      </button>
    </li>
  );
}

const ReviewQueue = () => {
  const { hasPermission } = useAuth();
  const canTriage = hasPermission('lexistar.review.triage');
  const wide = useIsWide();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateParam = searchParams.get('state');
  const state: string = REVIEW_STATES.some((s) => s.value === stateParam) ? (stateParam as string) : 'open';
  const kind = searchParams.get('kind') || '';
  const cause = searchParams.get('cause') || '';
  const tripId = searchParams.get('trip') || '';
  const asset = searchParams.get('asset') || '';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const selectedId = searchParams.get('item');

  const [rows, setRows] = useState<ReviewItem[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [bulk, setBulk] = useState<BulkTarget | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setParams = useCallback(
    (changes: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchQueue = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!canTriage) return;
      try {
        if (!quiet) setLoading(true);
        const states = REVIEW_STATES.find((s) => s.value === state)?.states || [];
        const res = await listReviewItems({
          state: states.length ? states : undefined,
          kind,
          cause_key: cause,
          trip_id: tripId,
          asset_key: asset,
          from,
          to,
          limit: PAGE_LIMIT,
        });
        const list = pickList<ReviewItem>(res, 'review_items', 'items');
        setRows(
          state === 'open'
            ? sortReviewQueue(list, Date.now())
            : [...list].sort((a, b) =>
                String(b.resolved_at || b.opened_at).localeCompare(
                  String(a.resolved_at || a.opened_at)
                )
              )
        );
        setStats(readReviewStats(res, state === 'open' ? list : []));
        setError(null);
      } catch (err) {
        if (!quiet) setError(errorMessage(err, 'Failed to load the review queue.'));
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canTriage, state, kind, cause, tripId, asset, from, to]
  );

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (!canTriage) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearInterval(tick);
      clearTimeout(refetchTimer.current);
    };
  }, [canTriage]);

  const connection = useAssuranceEvents({
    review: true,
    enabled: canTriage,
    onReview: (msg) => {
      // Counter payloads update the header at once; the list follows, debounced.
      if (msg && (msg.open != null || msg.share_this_week != null)) {
        setStats((s) => ({ ...(s || {}), ...readReviewStats(msg) }));
      }
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(
        () => fetchQueue({ quiet: true }),
        LIVE_REFETCH_DEBOUNCE_MS
      );
    },
  });

  const selected = useMemo(() => {
    const hit = rows.find((r) => r.id === selectedId);
    if (hit) return hit;
    return wide ? rows[0] || null : null;
  }, [rows, selectedId, wide]);

  const groups = useMemo(() => (state === 'open' ? causeGroups(rows) : []), [rows, state]);

  const select = useCallback(
    (id: string) => {
      setPending(null);
      setActionError(null);
      setParams({ item: id });
    },
    [setParams]
  );

  const move = useCallback(
    (delta: number) => {
      if (!rows.length) return;
      const idx = Math.max(
        0,
        rows.findIndex((r) => r.id === selected?.id)
      );
      const next = rows[Math.min(rows.length - 1, Math.max(0, idx + delta))];
      if (next) select(next.id);
    },
    [rows, selected, select]
  );

  const resolve = useCallback(
    async (outcome: Outcome, payload: object) => {
      if (!selected) return;
      setBusy(true);
      setActionError(null);
      try {
        if (outcome === 'accept') await acceptReviewItem(selected.id, payload);
        else if (outcome === 'dismiss') await dismissReviewItem(selected.id, payload);
        else await correctReviewItem(selected.id, payload);
        const idx = rows.findIndex((r) => r.id === selected.id);
        const remaining = state === 'open' ? rows.filter((r) => r.id !== selected.id) : rows;
        setRows(remaining);
        setStats((s) =>
          s && state === 'open' ? { ...s, open: Math.max(0, (Number(s.open) || 1) - 1) } : s
        );
        setChecked((c) => {
          const n = new Set(c);
          n.delete(selected.id);
          return n;
        });
        setPending(null);
        setNotice(
          `${VERBS[outcome]}: ${REVIEW_KINDS[selected.kind]?.short || selected.kind} ${String(selected.asset_key || selected.subject_id).replace(/^reg:/, '')}.`
        );
        const next = remaining[Math.min(idx, remaining.length - 1)];
        setParams({ item: wide ? next?.id || '' : '' });
        if (state !== 'open') fetchQueue({ quiet: true });
      } catch (err) {
        setActionError(errorMessage(err, 'The decision was not saved.'));
      } finally {
        setBusy(false);
      }
    },
    [selected, rows, state, setParams, wide, fetchQueue]
  );

  useEffect(() => {
    if (!canTriage) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPending(null);
        return;
      }
      const action = reviewShortcut(e);
      if (!action) return;
      if (action === 'next') move(1);
      else if (action === 'previous') move(-1);
      else if (!selected || (selected.state && selected.state !== 'open') || busy) return;
      else if (action === 'accept') resolve('accept', {});
      else {
        setActionError(null);
        setPending(action);
      }
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canTriage, selected, move, resolve, busy]);

  const toggle = (id: string) =>
    setChecked((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const openBulkForCause = async (group: CauseGroup) => {
    setBulkError(null);
    setBulk({ cause_key: group.cause_key, label: group.label, count: group.count, counting: true });
    try {
      // The server resolves every open item with this cause, not only the ones in view:
      // confirm its count, not ours.
      const res = (await listReviewItems({
        state: 'open',
        cause_key: group.cause_key,
        limit: PAGE_LIMIT,
      })) as {
        pagination?: { total?: number };
        total?: number;
        stats?: { open?: number };
      } | null;
      const list = pickList<ReviewItem>(res, 'review_items', 'items');
      const total = res?.pagination?.total ?? res?.total ?? res?.stats?.open ?? list.length;
      setBulk((b) => (b ? { ...b, count: Number(total), counting: false } : b));
    } catch {
      setBulk((b) => (b ? { ...b, counting: false } : b));
    }
  };

  const runBulk = async ({ outcome, reason }: { outcome: string; reason?: string }) => {
    if (!bulk) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      const res = await bulkResolveReviewItems(
        bulk.cause_key
          ? { cause_key: bulk.cause_key, outcome, reason }
          : { ids: bulk.ids, outcome, reason }
      );
      const n = res?.resolved ?? res?.count ?? res?.updated ?? bulk.count;
      setNotice(
        `${outcome === 'accept' ? 'Accepted' : 'Dismissed'} ${n} item${Number(n) === 1 ? '' : 's'}${bulk.cause_key ? ` with the cause ${bulk.label}` : ''}.`
      );
      setBulk(null);
      setChecked(new Set());
      await fetchQueue({ quiet: true });
    } catch (err) {
      setBulkError(errorMessage(err, 'Bulk triage failed; nothing was changed.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const oldestAge = stats?.oldest_at ? fmtDuration(secondsSince(stats.oldest_at, now)) : '—';

  const header = (
    <PageHeader
      icon={FaClipboardCheck}
      title="Review Queue"
      subtitle="Held out of statements until someone decides."
      toolbar={
        canTriage ? (
          <SegmentedControl<string>
            options={REVIEW_STATES.map((s) => ({
              value: s.value,
              label: s.label,
              badge: s.value === 'open' && stats?.open != null ? stats.open : undefined,
            }))}
            value={state}
            onChange={(v) => setParams({ state: v === 'open' ? '' : v, item: '' })}
            ariaLabel="Item state"
          />
        ) : null
      }
      actions={
        canTriage ? (
          <div className="flex items-center gap-2">
            <ConnectionIndicator status={connection} />
            <button
              type="button"
              onClick={() => setShowKeys((v) => !v)}
              aria-expanded={showKeys}
              className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FaKeyboard size={12} /> Shortcuts
            </button>
            <button
              type="button"
              onClick={() => fetchQueue()}
              aria-label="Refresh"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canTriage) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to triage review items."
      />
    );
  }

  const showList = wide || !selected;
  const showPanel = !!selected && (wide || !!selectedId);
  // Phone with an item open: the item alone (Back returns to the list).
  const phoneItem = !wide && showPanel;
  const chips = [
    tripId && { key: 'trip', label: `Trip ${tripId.slice(0, 8)}…` },
    asset && { key: 'asset', label: `Truck ${asset.replace(/^reg:/, '')}` },
    cause && { key: 'cause', label: causeLabel(cause) },
  ].filter((c): c is { key: string; label: string } => !!c);

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

        {!phoneItem && (
          <section aria-label="Queue summary" className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
            <KpiCard
              label="Open"
              value={stats?.open ?? '—'}
              hint="waiting for a decision"
              shortHint="waiting"
            />
            <KpiCard label="Oldest" value={oldestAge} hint="since it opened" shortHint="waiting" />
            <KpiCard
              label="Needed review"
              value={fmtShare(stats?.share_this_week)}
              hint="of this week's records — should fall after commissioning"
              shortHint="this week"
            />
          </section>
        )}

        {showKeys && (
          <div
            role="note"
            className="mb-4 px-4 py-3 bg-surface border border-slate-200 rounded-lg text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1"
          >
            <span>
              <kbd className="font-mono font-bold">A</kbd> accept as recorded
            </span>
            <span>
              <kbd className="font-mono font-bold">C</kbd> correct
            </span>
            <span>
              <kbd className="font-mono font-bold">D</kbd> dismiss
            </span>
            <span>
              <kbd className="font-mono font-bold">J</kbd> /{' '}
              <kbd className="font-mono font-bold">K</kbd> next / previous
            </span>
            <span>
              <kbd className="font-mono font-bold">Esc</kbd> cancel
            </span>
          </div>
        )}

        <div className={`flex flex-wrap items-end gap-3 mb-4 ${phoneItem ? 'hidden' : ''}`}>
          <div className="w-full sm:w-60">
            <label htmlFor="review-kind" className="block text-xs font-medium text-slate-600 mb-1">
              Kind
            </label>
            <ThemedSelect
              id="review-kind"
              value={kind}
              onChange={(e) => setParams({ kind: String(e.target.value), item: '' })}
              aria-label="Kind"
            >
              <option value="">All kinds</option>
              {Object.entries(REVIEW_KINDS).map(([k, m]) => (
                <option key={k} value={k}>
                  {m.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <div className="w-full sm:w-64">
            <span className="block text-xs font-medium text-slate-600 mb-1">Opened</span>
            {from || to ? (
              <div className="flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <UnifiedDateRangePicker
                    startDate={parseLocalDate(from) || new Date()}
                    endDate={parseLocalDate(to) || new Date()}
                    onStartDateChange={(d) =>
                      setParams({ from: d ? toApiDateString(d) : '', item: '' })
                    }
                    onEndDateChange={(d) =>
                      setParams({ to: d ? toApiDateString(d) : '', item: '' })
                    }
                    bounds="past"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setParams({ from: '', to: '', item: '' })}
                  aria-label="Any time"
                  className="touch-target p-2 rounded-md text-slate-500 hover:bg-slate-200"
                >
                  <FaTimes size={11} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  setParams({
                    from: toApiDateString(new Date(Date.now() - 6 * 86400000)),
                    to: toApiDateString(new Date()),
                    item: '',
                  })
                }
                className="touch-target w-full text-left px-2 py-1 border border-slate-300 rounded-md text-sm sm:text-xs text-slate-500 bg-surface hover:border-slate-400"
              >
                Any time — filter by date
              </button>
            )}
          </div>
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-slate-200 text-sm text-slate-800"
            >
              {c.label}
              <button
                type="button"
                onClick={() => setParams({ [c.key]: '', item: '' })}
                aria-label={`Remove filter ${c.label}`}
                className="p-1.5 rounded-full hover:bg-slate-300"
              >
                <FaTimes size={10} />
              </button>
            </span>
          ))}
        </div>

        {groups.length > 0 && showList && (
          <section aria-label="Common causes" className={`${cardClasses} p-3 sm:p-4 mb-4`}>
            <h2 className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              <FaLayerGroup size={11} /> Shared causes — triage them together
            </h2>
            <ul className="flex flex-wrap gap-2">
              {groups.slice(0, 8).map((g) => (
                <li key={g.cause_key}>
                  <button
                    type="button"
                    onClick={() => openBulkForCause(g)}
                    className="inline-flex items-center gap-2 px-3 min-h-[44px] rounded-lg border border-slate-300 bg-surface text-sm text-slate-800 hover:bg-slate-50"
                  >
                    <span className="tabular-nums font-bold">{g.count}</span>
                    <span>{g.label}</span>
                    <span className="text-xs text-primary-700 font-medium">Triage…</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {checked.size > 0 && state === 'open' && (
          <div className="sticky top-16 z-[5] mb-3 flex items-center justify-between gap-3 px-4 py-2 bg-slate-900 text-white rounded-lg shadow">
            <span className="text-sm tabular-nums">{checked.size} selected</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setChecked(new Set())}
                className="px-3 py-1.5 text-sm rounded-md hover:bg-white/10"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkError(null);
                  setBulk({ ids: [...checked], count: checked.size, label: 'selection' });
                }}
                className="px-3 py-1.5 text-sm font-semibold rounded-md bg-white text-slate-900"
              >
                Triage selected…
              </button>
            </div>
          </div>
        )}

        {loading && rows.length === 0 ? (
          <LoadingState label="Loading the review queue..." />
        ) : rows.length === 0 ? (
          <div className={cardClasses}>
            <EmptyState
              icon={FaClipboardCheck}
              title={state === 'open' ? 'Nothing to review' : 'Nothing here'}
            >
              {state === 'open'
                ? 'Every record in this view is confident enough to count. New items appear here as the engine opens them.'
                : 'No items match these filters.'}
            </EmptyState>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-4 lg:gap-6">
            {showList && (
              <aside className={`${cardClasses} overflow-hidden self-start`}>
                <h2 className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  {rows.length} item{rows.length === 1 ? '' : 's'}
                  {state === 'open' ? ' · oldest and weightiest first' : ''}
                </h2>
                <ul className="divide-y divide-slate-100" aria-label="Review queue">
                  {rows.map((r) => (
                    <QueueRow
                      key={r.id}
                      row={r}
                      selected={selected?.id === r.id}
                      checked={checked.has(r.id)}
                      onToggle={toggle}
                      onSelect={select}
                      now={now}
                      selectable={state === 'open'}
                    />
                  ))}
                </ul>
              </aside>
            )}
            {showPanel && selected && (
              <ReviewItemPanel
                key={selected.id}
                item={selected}
                canTriage={canTriage}
                pending={pending}
                setPending={(p) => {
                  setActionError(null);
                  setPending(p);
                }}
                busy={busy}
                actionError={actionError}
                onResolve={resolve}
                onBack={wide ? undefined : () => setParams({ item: '' })}
                now={now}
              />
            )}
          </div>
        )}
      </div>

      <BulkTriageDialog
        open={!!bulk}
        target={bulk}
        busy={bulkBusy}
        error={bulkError}
        onCancel={() => setBulk(null)}
        onConfirm={runBulk}
      />
    </div>
  );
};

export default ReviewQueue;
